import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import HotelBookingProgressSheet, {
  HotelBookingProgressSummary,
} from '../components/chat/sidebar/HotelBookingProgressSheet';
import {
  useHotelArtifactFetcher,
  useHotelBookingState,
} from '../components/chat/sidebar/useHotelBookingState';

export interface HotelBookingProgressRequest {
  artifactId: string;
  shareToken?: string;
  summary: HotelBookingProgressSummary;
  assistantName: string;
}

interface HotelBookingProgressContextType {
  /** Opens the progress sheet for a Book press that has just been accepted. */
  start: (request: HotelBookingProgressRequest) => void;
  /** Closes it early (e.g. the Book request itself failed). */
  abort: (artifactId: string) => void;
}

const HotelBookingProgressContext =
  createContext<HotelBookingProgressContextType | null>(null);

/** Null outside the provider; callers then render the sheet themselves. */
export const useOptionalHotelBookingProgress =
  (): HotelBookingProgressContextType | null =>
    useContext(HotelBookingProgressContext);

const ActiveProgress: React.FC<{
  request: HotelBookingProgressRequest;
  onDone: () => void;
}> = ({ request, onDone }) => {
  const fetchSnapshot = useHotelArtifactFetcher(
    request.artifactId,
    request.shareToken,
  );
  const state = useHotelBookingState(request.artifactId, 0, fetchSnapshot);
  return (
    <HotelBookingProgressSheet
      show
      state={state}
      summary={request.summary}
      assistantName={request.assistantName}
      notifyVia={request.shareToken ? 'whatsapp' : 'chat'}
      onDone={onDone}
    />
  );
};

/**
 * Hosts the hotel booking progress sheet above the artifact modals, so it
 * survives the host closing the modal once the Book request is accepted.
 */
export const HotelBookingProgressProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [active, setActive] = useState<HotelBookingProgressRequest | null>(
    null,
  );
  const start = useCallback(
    (request: HotelBookingProgressRequest) => setActive(request),
    [],
  );
  const abort = useCallback(
    (artifactId: string) =>
      setActive((current) =>
        current?.artifactId === artifactId ? null : current,
      ),
    [],
  );
  const value = useMemo(() => ({ start, abort }), [start, abort]);
  return (
    <HotelBookingProgressContext.Provider value={value}>
      {children}
      {active && (
        <ActiveProgress
          key={active.artifactId}
          request={active}
          onDone={() => setActive(null)}
        />
      )}
    </HotelBookingProgressContext.Provider>
  );
};
